import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, GraduationCap, Wallet, School, Baby, LogOut, Menu, Building2,
  PiggyBank, ReceiptText, FileBarChart2, ScrollText, MessageCircle, Settings as SettingsIcon,
  ShoppingCart, Megaphone,
} from "lucide-react";
import { CreditCard, BadgeCheck, QrCode } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { ROLE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MENU = {
  super_admin: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/sekolah", label: "Sekolah", icon: School, testId: "nav-sekolah" },
    { to: "/langganan", label: "Langganan", icon: CreditCard, testId: "nav-langganan" },
    { to: "/siswa", label: "Data Siswa", icon: GraduationCap, testId: "nav-siswa" },
    { to: "/spp", label: "SPP Bulanan", icon: Wallet, testId: "nav-spp" },
    { to: "/tabungan", label: "Tabungan", icon: PiggyBank, testId: "nav-tabungan" },
    { to: "/tagihan", label: "Tagihan Lainnya", icon: ReceiptText, testId: "nav-tagihan" },
    { to: "/kas", label: "Kas & Belanja", icon: ShoppingCart, testId: "nav-kas" },
    { to: "/konfirmasi", label: "Konfirmasi Bayar", icon: BadgeCheck, testId: "nav-konfirmasi" },
    { to: "/pemberitahuan", label: "Pemberitahuan", icon: Megaphone, testId: "nav-pemberitahuan" },
    { to: "/laporan", label: "Laporan", icon: FileBarChart2, testId: "nav-laporan" },
    { to: "/pengguna", label: "Pengguna", icon: Users, testId: "nav-pengguna" },
    { to: "/log", label: "Log Aktivitas", icon: ScrollText, testId: "nav-log" },
    { to: "/whatsapp", label: "WhatsApp Gateway", icon: MessageCircle, testId: "nav-whatsapp" },
    { to: "/pengaturan", label: "Pengaturan Akun", icon: SettingsIcon, testId: "nav-pengaturan" },
  ],
  kepala_sekolah: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/siswa", label: "Data Siswa", icon: GraduationCap, testId: "nav-siswa" },
    { to: "/spp", label: "SPP Bulanan", icon: Wallet, testId: "nav-spp" },
    { to: "/tabungan", label: "Tabungan", icon: PiggyBank, testId: "nav-tabungan" },
    { to: "/tagihan", label: "Tagihan Lainnya", icon: ReceiptText, testId: "nav-tagihan" },
    { to: "/kas", label: "Kas & Belanja", icon: ShoppingCart, testId: "nav-kas" },
    { to: "/konfirmasi", label: "Konfirmasi Bayar", icon: BadgeCheck, testId: "nav-konfirmasi" },
    { to: "/pemberitahuan", label: "Pemberitahuan", icon: Megaphone, testId: "nav-pemberitahuan" },
    { to: "/laporan", label: "Laporan", icon: FileBarChart2, testId: "nav-laporan" },
    { to: "/log", label: "Log Aktivitas", icon: ScrollText, testId: "nav-log" },
    { to: "/pengaturan", label: "Pengaturan Akun", icon: SettingsIcon, testId: "nav-pengaturan" },
  ],
  bendahara: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/siswa", label: "Data Siswa", icon: GraduationCap, testId: "nav-siswa" },
    { to: "/spp", label: "SPP Bulanan", icon: Wallet, testId: "nav-spp" },
    { to: "/tabungan", label: "Tabungan", icon: PiggyBank, testId: "nav-tabungan" },
    { to: "/tagihan", label: "Tagihan Lainnya", icon: ReceiptText, testId: "nav-tagihan" },
    { to: "/kas", label: "Kas & Belanja", icon: ShoppingCart, testId: "nav-kas" },
    { to: "/konfirmasi", label: "Konfirmasi Bayar", icon: BadgeCheck, testId: "nav-konfirmasi" },
    { to: "/pemberitahuan", label: "Pemberitahuan", icon: Megaphone, testId: "nav-pemberitahuan" },
    { to: "/laporan", label: "Laporan", icon: FileBarChart2, testId: "nav-laporan" },
    { to: "/pengguna", label: "Pengguna", icon: Users, testId: "nav-pengguna" },
    { to: "/log", label: "Log Aktivitas", icon: ScrollText, testId: "nav-log" },
    { to: "/whatsapp", label: "WhatsApp Gateway", icon: MessageCircle, testId: "nav-whatsapp" },
    { to: "/pengaturan", label: "Pengaturan Akun", icon: SettingsIcon, testId: "nav-pengaturan" },
  ],
  orang_tua: [
    { to: "/anak", label: "Anak Saya", icon: Baby, testId: "nav-anak" },
    { to: "/anak/bayar", label: "Bayar Online", icon: QrCode, testId: "nav-bayar" },
    { to: "/anak/tabungan", label: "Tabungan", icon: PiggyBank, testId: "nav-tabungan" },
    { to: "/anak/pemberitahuan", label: "Pemberitahuan", icon: Megaphone, testId: "nav-pemberitahuan" },
    { to: "/pengaturan", label: "Pengaturan Akun", icon: SettingsIcon, testId: "nav-pengaturan" },
  ],
};

function Brand() {
  const { schoolId } = useAuth();
  const [logo, setLogo] = useState(null);
  const [ver, setVer] = useState(0);
  useEffect(() => {
    api.get("/schools/current").then((r) => setLogo(r.data?.logo_path || null)).catch(() => setLogo(null));
  }, [schoolId, ver]);
  useEffect(() => {
    const bump = () => setVer((v) => v + 1);
    window.addEventListener("sb-logo-updated", bump);
    return () => window.removeEventListener("sb-logo-updated", bump);
  }, []);
  const src = logo ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${logo}?auth=${localStorage.getItem("sb_token")}&v=${ver}` : null;
  return (
    <div className="flex items-center gap-2.5 px-5 py-5">
      {src ? (
        <img src={src} alt="Logo sekolah" className="h-9 w-9 rounded-lg border border-slate-200 object-cover" data-testid="school-logo" />
      ) : (
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0060AF]">
          <Wallet className="h-5 w-5 text-white" strokeWidth={1.5} />
        </div>
      )}
      <div>
        <p className="font-heading text-base font-bold leading-tight text-slate-900">KasPintar</p>
        <p className="text-[11px] text-slate-500">by EduNexa</p>
        <p className="text-[11px] text-slate-500">Keuangan Sekolah</p>
      </div>
    </div>
  );
}

function NavItems({ onNavigate }) {
  const { user } = useAuth();
  const items = MENU[user.role] || [];
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/" || item.to === "/anak"}
          onClick={onNavigate}
          data-testid={item.testId}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
              isActive
                ? "bg-[#E5F0FA] text-[#0060AF]"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`
          }
        >
          <item.icon className="h-4 w-4" strokeWidth={1.5} />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function SchoolSwitcher() {
  const { user, schoolId, setSchoolId } = useAuth();
  const [schools, setSchools] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (user.role === "super_admin") {
      api.get("/schools").then((r) => {
        setSchools(r.data);
        if (!schoolId && r.data.length > 0) setSchoolId(r.data[0].id);
        else if (schoolId && r.data.length > 0 && !r.data.some((s) => s.id === schoolId)) setSchoolId(r.data[0].id);
        else if (schoolId && r.data.length === 0) setSchoolId("");
      }).catch(() => {});
    }
    api.get("/schools/current").then((r) => setCurrent(r.data)).catch(() => setCurrent(null));
  }, [user.role, schoolId, setSchoolId]);

  if (user.role === "super_admin") {
    return (
      <Select value={schoolId} onValueChange={setSchoolId}>
        <SelectTrigger data-testid="school-switcher" className="w-[220px] bg-white">
          <SelectValue placeholder="Pilih sekolah..." />
        </SelectTrigger>
        <SelectContent>
          {schools.map((s) => (
            <SelectItem key={s.id} value={s.id} data-testid={`school-option-${s.id}`}>
              {s.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  return (
    <div className="flex items-center gap-2 text-sm font-medium text-slate-700" data-testid="current-school-name">
      <Building2 className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
      {current?.name || <span className="inline-block h-4 w-32 animate-pulse rounded bg-slate-200" aria-hidden="true" />}
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-slate-200 bg-white md:flex">
        <Brand />
        <div className="mt-2 flex-1">
          <NavItems />
        </div>
        <div className="border-t border-slate-100 p-4">
          <p className="text-sm font-medium text-slate-800" data-testid="sidebar-user-name">{user.name}</p>
          <p className="text-xs text-slate-500">{ROLE_LABELS[user.role]}</p>
          <Button
            variant="ghost"
            size="sm"
            data-testid="logout-button"
            onClick={handleLogout}
            className="mt-3 w-full justify-start gap-2 text-slate-600 hover:text-rose-600"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Keluar
          </Button>
        </div>
      </aside>

      <div className="md:pl-60">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0" aria-describedby={undefined}>
                <SheetTitle className="sr-only">Menu Navigasi</SheetTitle>
                <Brand />
                <NavItems onNavigate={() => setOpen(false)} />
                <div className="mt-4 border-t border-slate-100 p-4">
                  <Button variant="ghost" size="sm" data-testid="logout-button-mobile" onClick={handleLogout} className="gap-2 text-slate-600">
                    <LogOut className="h-4 w-4" strokeWidth={1.5} /> Keluar
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            <SchoolSwitcher />
          </div>
          <div className="hidden items-center gap-2 sm:flex">
            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700" data-testid="header-role-badge">
              {ROLE_LABELS[user.role]}
            </span>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
